// ─────────────────────────────────────────
//  TEMPLATE GALLERY
// ─────────────────────────────────────────

import { S, sb, ls, setLs } from './state.js';
import { showToast, autoResize, extractCode, simpleHash } from './utils.js';
import { addMsg } from './ui.js';
import { applyCode, notifyCodeUpdate } from './editor.js';
import { runCode } from './preview.js';
import { autoSaveProject } from './projects.js';
import { isProPlus, showProPlusUpgrade, toggleFullStackMode, isFullStackMode } from './fullstack.js';

// ── Template cache (localStorage + Supabase) ──
const TEMPLATE_CACHE_KEY = 'kc_template_cache';

function getTemplateCache() {
  try { return JSON.parse(ls(TEMPLATE_CACHE_KEY) || '{}'); } catch { return {}; }
}

function setTemplateCache(promptHash, code) {
  const cache = getTemplateCache();
  cache[promptHash] = { code, ts: Date.now() };
  // Keep max 20 cached templates to avoid localStorage bloat
  const keys = Object.keys(cache);
  if (keys.length > 20) {
    const sorted = keys.sort((a, b) => cache[a].ts - cache[b].ts);
    delete cache[sorted[0]];
  }
  setLs(TEMPLATE_CACHE_KEY, JSON.stringify(cache));
}

function getCachedTemplate(prompt, callback) {
  const hash = simpleHash(prompt);

  // Check local cache first (instant)
  const local = getTemplateCache()[hash];
  if (local?.code) {
    callback(local.code);
    return;
  }

  // Check Supabase cache (fast, shared across users)
  sb.from('best_builds')
    .select('code')
    .eq('category', 'template_' + hash)
    .limit(1)
    .single()
    .then(({ data }) => {
      if (data?.code) {
        // Save to local cache for next time
        setTemplateCache(hash, data.code);
        callback(data.code);
      } else {
        callback(null);
      }
    })
    .catch(() => {
      callback(null);
    });
}

function cacheTemplate(prompt, code) {
  const hash = simpleHash(prompt);
  setTemplateCache(hash, code);

  // Also save to Supabase for other users (fire and forget)
  sb.from('best_builds').upsert({
    category: 'template_' + hash,
    name: prompt.slice(0, 60),
    code,
    quality_score: 500,
    code_lines: code.split('\n').length,
    has_localstorage: code.includes('localStorage'),
    has_responsive: code.includes('viewport'),
  }, { onConflict: 'category' })
    .then(() => {})
    .catch(() => {});
}

export function filterTemplates(cat, btn) {
  // Update active tab
  document.querySelectorAll('.tg-cat').forEach(c => c.classList.remove('active'));
  if (btn) btn.classList.add('active');

  // Community tab — load from API
  if (cat === 'community') {
    loadCommunityTemplates();
    return;
  }

  // Show built-in templates, hide community section
  const communitySection = document.getElementById('community-templates');
  if (communitySection) communitySection.style.display = 'none';

  // Filter cards
  document.querySelectorAll('.tg-card').forEach(card => {
    if (cat === 'all' || card.dataset.cat === cat) {
      card.classList.remove('hidden');
    } else {
      card.classList.add('hidden');
    }
  });
}

function loadCommunityTemplates() {
  // Hide built-in template cards
  document.querySelectorAll('.tg-card').forEach(card => card.classList.add('hidden'));

  // Show or create community section
  let section = document.getElementById('community-templates');
  if (!section) {
    section = document.createElement('div');
    section.id = 'community-templates';
    section.style.cssText = 'padding:12px 0;';
    document.getElementById('tg-grid').appendChild(section);
  }
  section.style.display = 'block';
  section.innerHTML = '<div style="text-align:center;padding:24px;color:var(--muted);font-size:13px;">Loading community templates...</div>';

  fetch('/api/projects/templates?limit=24')
    .then(res => res.json())
    .then(data => {
      if (!data.templates || !data.templates.length) {
        section.innerHTML = '<div style="text-align:center;padding:24px;color:var(--muted);font-size:13px;">No community templates yet. Be the first to share yours!</div>';
        return;
      }

      section.innerHTML = data.templates.map(t => `
        <div class="tg-card" style="display:flex;" onclick="loadRemix('${t.slug}')">
          <div class="tg-card-icon">🌍</div>
          <div class="tg-card-body">
            <div class="tg-card-title">${escapeHtml(t.name)}</div>
            <div class="tg-card-desc">${escapeHtml(t.description || 'A community-built app')}</div>
            <div style="font-size:10px;color:var(--muted2);margin-top:4px;font-family:var(--mono);">
              by ${escapeHtml(t.creator)} · 👁 ${t.views} · 🔄 ${t.remixes}
            </div>
          </div>
          <div class="tg-card-tag">${escapeHtml(t.category || 'App')}</div>
        </div>
      `).join('');
    })
    .catch(() => {
      section.innerHTML = '<div style="text-align:center;padding:24px;color:var(--muted);font-size:13px;">Could not load templates. Check your connection.</div>';
    });
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

export function useTemplate(card) {
  const promptEl = card.querySelector('.tg-card-prompt');
  const titleEl = card.querySelector('.tg-card-title');
  if (!promptEl || !titleEl) return;

  const prompt = promptEl.textContent;
  const title = titleEl.textContent;

  // Hide gallery
  hideGallery();

  // Check cache — use callback pattern (no await)
  getCachedTemplate(prompt, (cached) => {
    if (cached) {
      addMsg('assistant', `⚡ "${title}" loaded instantly from cache!\n\nYour app is running in the preview. Describe any changes you want to make.`, true);
      S.code = cached;
      applyCode(cached);
      runCode();
      notifyCodeUpdate();
      S.history.push({ role: 'user', content: prompt });
      S.history.push({ role: 'assistant', content: '```html\n' + cached + '\n```' });
      autoSaveProject();
      return;
    }

    // No cache — load prompt for AI generation
    const input = document.getElementById('chat-input');
    if (!input) return;
    input.value = prompt;
    autoResize(input);
    input.focus();

    addMsg('assistant', `📝 Template loaded: "${title}"\n\nCustomize the prompt below — add your business name, preferred colors, or any extra features. Then hit send when ready!`, false);

    setTimeout(() => input.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  });
}

export function useFullStackTemplate(card) {
  if (!isProPlus()) {
    showProPlusUpgrade();
    return;
  }

  const promptEl = card.querySelector('.tg-card-prompt');
  const titleEl = card.querySelector('.tg-card-title');
  if (!promptEl || !titleEl) return;

  const prompt = promptEl.textContent;
  const title = titleEl.textContent.replace(/PRO\+/g, '').trim();

  // Enable full-stack mode
  if (!isFullStackMode()) toggleFullStackMode();

  // Hide gallery
  hideGallery();

  // Load prompt into input
  const input = document.getElementById('chat-input');
  if (!input) return;
  input.value = prompt;
  autoResize(input);
  input.focus();

  addMsg('assistant', `🗄️ Full-Stack template loaded: "${title}"\n\n🟢 Full-Stack Mode is ON — this will generate a complete Next.js + Supabase project.\n\nCustomize the prompt below — add your business name, colors, specific services, or any details. Then hit send!`, false);

  setTimeout(() => input.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
}

export function hideGallery() {
  const gallery = document.getElementById('template-gallery');
  if (gallery) {
    gallery.style.transition = 'opacity .3s, transform .3s';
    gallery.style.opacity = '0';
    gallery.style.transform = 'translateY(-10px)';
    setTimeout(() => { gallery.style.display = 'none'; }, 300);
  }
  // Show welcome msg for future messages
  const wm = document.getElementById('welcome-msg');
  if (wm) wm.style.display = '';
}

export function loadRemix(slug) {
  hideGallery();
  addMsg('assistant', '🔄 Loading remixed app...', false);

  fetch('/api/projects/share?slug=' + encodeURIComponent(slug))
    .then(res => res.json())
    .then(data => {
      if (data.project?.code) {
        S.code = data.project.code;
        S.mode = data.project.mode || 'html';
        applyCode(S.code);
        runCode();
        notifyCodeUpdate();

        addMsg('assistant', `✅ Loaded "${data.project.name || 'Remixed App'}"! It's running in the preview.\n\nThis is your copy now — describe any changes you want to make.`, true);

        // Track the remix (fire-and-forget)
        if (S.session && data.project.id) {
          fetch('/api/projects/templates', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + S.session.access_token,
            },
            body: JSON.stringify({ projectId: data.project.id }),
          }).catch(() => {});
        }

        // Clean the URL
        history.replaceState(null, '', '/playground.html');
      } else {
        addMsg('assistant', '⚠️ Could not load that app. It may have been removed.', false);
      }
    })
    .catch(() => {
      addMsg('assistant', '⚠️ Could not load the remix. Check your connection and try again.', false);
    });
}

// Export cacheTemplate so agents can cache after successful builds
export { cacheTemplate };
